import React from 'react';
import { Sparkles, Star, Quote, CheckCircle2, BarChart, Award, Users } from 'lucide-react';

export const Testimonials: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-950">
      {/* Hero Section */}
      <section className="relative w-full py-20 flex items-center overflow-hidden">
        {/* Fondos */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-black" />
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1604933762021-54a5858c9832?ixlib=rb-4.0.3')] bg-cover bg-center opacity-5" />
        
        {/* Efecto de resplandor */}
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-purple-800/20 rounded-full blur-[100px] animate-pulse" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-blue-800/20 rounded-full blur-[80px] animate-pulse delay-300" />
        </div>
        
        {/* Contenido */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 relative">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 bg-purple-900/20 backdrop-blur-sm rounded-full text-purple-400 mb-6 border border-purple-900/30">
              <Users className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">Historias de Éxito</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Lo que dicen nuestros{' '}
              <span className="relative">
                <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-300">
                  usuarios
                </span>
                <span className="absolute inset-0 bg-gradient-to-r from-purple-400/20 to-blue-300/20 blur-lg" />
              </span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Descubre cómo Curvia AI ha transformado la búsqueda de empleo de profesionales en todo el mundo
            </p>
            
            <div className="flex flex-wrap justify-center gap-8 mb-12">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg backdrop-blur-sm border border-white/10">
                <Star className="w-5 h-5 text-yellow-400" fill="currentColor" />
                <span className="text-white font-bold">4.9/5</span>
                <span className="text-gray-400">calificación promedio</span>
              </div>
              
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg backdrop-blur-sm border border-white/10">
                <Users className="w-5 h-5 text-blue-400" />
                <span className="text-white font-bold">10,000+</span>
                <span className="text-gray-400">usuarios satisfechos</span>
              </div>
              
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg backdrop-blur-sm border border-white/10">
                <Award className="w-5 h-5 text-purple-400" />
                <span className="text-white font-bold">89%</span>
                <span className="text-gray-400">tasa de contratación</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Testimonials */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-gray-950 via-gray-900/50 to-purple-950/30">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/3 w-[400px] h-[400px] bg-purple-900/15 rounded-full blur-[100px]" />
          <div className="absolute bottom-1/3 right-1/4 w-[300px] h-[300px] bg-blue-900/15 rounded-full blur-[80px]" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="group relative bg-gradient-to-br from-gray-900/80 to-purple-950/50 p-8 rounded-xl backdrop-blur-sm border border-purple-500/20 hover:border-purple-500/40 transition-all shadow-lg">
              <div className="absolute -top-5 -left-5">
                <Quote className="w-10 h-10 text-purple-500/40" />
              </div>
              
              <div className="flex items-center gap-2 mb-6">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-5 h-5 text-yellow-400" fill="currentColor" />
                ))}
              </div>
              
              <p className="text-gray-300 mb-6 relative">
                "El diseño de mi CV llamó la atención inmediatamente. El reclutador mencionó específicamente lo profesional que se veía durante la entrevista. Conseguí el trabajo en una empresa top de tecnología."
              </p>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 overflow-hidden rounded-full">
                  <img 
                    src="/profiles/mujer_1.jpg" 
                    alt="María A." 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-white font-medium">María A.</p>
                  <p className="text-gray-400 text-sm">Diseñadora UX/UI</p>
                </div>
              </div>
            </div>
            
            {/* Testimonial 2 */}
            <div className="group relative bg-gradient-to-br from-gray-900/80 to-purple-950/50 p-8 rounded-xl backdrop-blur-sm border border-purple-500/20 hover:border-purple-500/40 transition-all shadow-lg">
              <div className="absolute -top-5 -left-5">
                <Quote className="w-10 h-10 text-purple-500/40" />
              </div>
              
              <div className="flex items-center gap-2 mb-6">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-5 h-5 text-yellow-400" fill="currentColor" />
                ))}
              </div>
              
              <p className="text-gray-300 mb-6">
                "Conseguí entrevistas en 3 empresas diferentes en la primera semana. La optimización para cada puesto específico hizo toda la diferencia. Ahora trabajo en mi empresa soñada."
              </p>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 overflow-hidden rounded-full">
                  <img 
                    src="/profiles/hombre_1.jpg" 
                    alt="Juan R." 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-white font-medium">Juan R.</p>
                  <p className="text-gray-400 text-sm">Desarrollador Full Stack</p>
                </div>
              </div>
            </div>
            
            {/* Testimonial 3 */}
            <div className="group relative bg-gradient-to-br from-gray-900/80 to-purple-950/50 p-8 rounded-xl backdrop-blur-sm border border-purple-500/20 hover:border-purple-500/40 transition-all shadow-lg">
              <div className="absolute -top-5 -left-5">
                <Quote className="w-10 h-10 text-purple-500/40" />
              </div>
              
              <div className="flex items-center gap-2 mb-6">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-5 h-5 text-yellow-400" fill="currentColor" />
                ))}
              </div>
              
              <p className="text-gray-300 mb-6">
                "La función de análisis de ofertas laborales es increíble. Mi CV estaba perfectamente alineado con los requisitos del puesto. La inversión valió cada centavo."
              </p>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 overflow-hidden rounded-full">
                  <img 
                    src="/profiles/hombre_2.jpg" 
                    alt="Carlos P." 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-white font-medium">Carlos P.</p>
                  <p className="text-gray-400 text-sm">Gerente de Proyecto</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* More Testimonials */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-purple-950/30 via-gray-900/50 to-gray-950">
        <div className="absolute inset-0">
          <div className="absolute bottom-1/4 right-1/3 w-[400px] h-[400px] bg-purple-900/15 rounded-full blur-[100px]" />
          <div className="absolute top-1/3 left-1/4 w-[300px] h-[300px] bg-blue-900/15 rounded-full blur-[80px]" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Más historias de éxito
            </h2>
            <p className="text-xl text-gray-300">
              Conoce más experiencias de personas que han utilizado Curvia AI
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                name: "Laura M.",
                role: "Marketing Digital",
                quote: "Tras 3 meses de búsqueda sin éxito, con Curvia AI conseguí una entrevista en solo 5 días.",
                rating: 5,
                image: "/profiles/mujer_2.jpg",
                gender: "mujer"
              },
              {
                name: "Andrés G.",
                role: "Ingeniero de Software",
                quote: "La optimización de palabras clave es impresionante. Mi perfil ahora es mucho más relevante para los reclutadores.",
                rating: 5,
                image: "/profiles/hombre_3.jpg",
                gender: "hombre"
              },
              {
                name: "Elena R.",
                role: "Recursos Humanos",
                quote: "Como profesional de RRHH, puedo confirmar que los CVs de Curvia AI destacan inmediatamente entre los demás.",
                rating: 4,
                image: "/profiles/mujer_3.jpg",
                gender: "mujer"
              },
              {
                name: "Gabriel T.",
                role: "Analista de Datos",
                quote: "El sistema de monedas es muy conveniente y económico. Puedo adaptar mi CV para cada empresa que me interesa.",
                rating: 5,
                image: "/profiles/hombre_4.jpg",
                gender: "hombre"
              },
              {
                name: "Sofía L.",
                role: "Arquitecta",
                quote: "El diseño visual es excepcional, profesional pero creativo. Perfecto para mi sector.",
                rating: 5,
                image: "/profiles/mujer_4.jpg",
                gender: "mujer"
              },
              {
                name: "Martín P.",
                role: "Consultor Financiero",
                quote: "La velocidad de generación es impresionante. En menos de un minuto tenía un CV completamente optimizado.",
                rating: 5,
                image: "/profiles/hombre_4.jpg",
                gender: "hombre"
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-gray-900/60 backdrop-blur-sm p-6 rounded-xl border border-gray-800/80 hover:border-purple-500/30 transition-all">
                <div className="flex items-center gap-2 mb-4">
                  {Array.from({ length: testimonial.rating }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" />
                  ))}
                </div>
                <p className="text-gray-300 mb-4 text-sm italic">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 overflow-hidden rounded-full">
                    <img 
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">{testimonial.name}</p>
                    <p className="text-gray-400 text-xs">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-gray-950 to-purple-950/30">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/3 w-[400px] h-[400px] bg-blue-900/15 rounded-full blur-[100px]" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="bg-gradient-to-br from-gray-900/80 to-purple-950/50 p-8 rounded-xl backdrop-blur-sm border border-purple-500/20 shadow-lg">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-white mb-2">Resultados comprobados</h2>
              <p className="text-gray-300">Estadísticas que demuestran la efectividad de Curvia AI</p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white/5 backdrop-blur-sm p-6 rounded-lg border border-white/10 text-center">
                <BarChart className="w-10 h-10 text-purple-400 mx-auto mb-4" />
                <p className="text-3xl font-bold text-white mb-2">7x</p>
                <p className="text-gray-400 text-sm">Más probabilidades de conseguir entrevistas</p>
              </div>
              
              <div className="bg-white/5 backdrop-blur-sm p-6 rounded-lg border border-white/10 text-center">
                <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-4" />
                <p className="text-3xl font-bold text-white mb-2">89%</p>
                <p className="text-gray-400 text-sm">Tasa de contratación con nuestros CVs</p>
              </div>
              
              <div className="bg-white/5 backdrop-blur-sm p-6 rounded-lg border border-white/10 text-center">
                <Sparkles className="w-10 h-10 text-yellow-400 mx-auto mb-4" />
                <p className="text-3xl font-bold text-white mb-2">98%</p>
                <p className="text-gray-400 text-sm">Clientes satisfechos</p>
              </div>
              
              <div className="bg-white/5 backdrop-blur-sm p-6 rounded-lg border border-white/10 text-center">
                <Award className="w-10 h-10 text-blue-400 mx-auto mb-4" />
                <p className="text-3xl font-bold text-white mb-2">45s</p>
                <p className="text-gray-400 text-sm">Tiempo promedio de generación</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden bg-gray-950">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gray-900/80 mix-blend-overlay" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-900/10 rounded-full blur-[100px]" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <div className="bg-gray-900/60 backdrop-blur-sm p-12 rounded-xl border border-gray-800/80 shadow-lg max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-8">
              Únete a miles de profesionales satisfechos
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              No dejes que otro candidato menos calificado obtenga el puesto. 
              Optimiza tu CV ahora y destaca entre la competencia.
            </p>
            <a
              href="/dashboard" 
              className="group relative inline-flex items-center px-8 py-4 bg-gradient-to-r from-gray-800 to-purple-900 text-white rounded-lg transition-all transform hover:scale-105 hover:shadow-[0_0_30px_rgba(88,28,135,0.7)]"
            >
              <span className="relative z-10 flex items-center gap-2">
                <span>Crear mi CV optimizado</span>
                <Sparkles className="w-5 h-5 animate-pulse text-yellow-200" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-gray-800 to-purple-900 rounded-lg blur opacity-0 group-hover:opacity-50 transition-opacity" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}; 